import type { ChallengeOption } from '../api/models';
import { AppText } from '../components/AppText';
import { AppTextField } from '../components/AppTextField';
import { Strings } from '../strings';
import { GridTokenView, SymbolView } from '../symbols/SymbolView';
import { parseSymbol } from '../symbols/symbolParser';

/**
 * How the child answers, chosen by `item.presentation`.
 * Mirrors android/…/Challenges/AnswerControls.kt.
 *
 * Every one of these is **tap-only**. Ordering has taps rather than requiring a drag, because a drag is
 * the one interaction a child with poor motor control cannot complete, and the task is about word order
 * rather than about dexterity (07-Challenges). On the web that also means it works from a keyboard, which
 * a drag never would.
 *
 * Nothing here marks a wrong choice. A wrong answer clears the selection; it never paints it red.
 */
export function MultipleChoiceAnswer({
  options,
  selectedKeys,
  isEnabled,
  onSelect,
}: {
  options: ChallengeOption[];
  selectedKeys: string[];
  isEnabled: boolean;
  onSelect: (key: string) => void;
}) {
  return (
    <div
      role="radiogroup"
      style={{
        display: 'grid',
        // Exactly two per row, at every width, because that is what the apps do. `auto-fit` looked like
        // the responsive answer and produced three columns in a wide task column — a different layout for
        // the same task, which is precisely what the parity register forbids.
        gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
        gap: 'var(--space-3)',
        width: '100%',
      }}
    >
      {options.map((option: ChallengeOption) => (
        <OptionTile
          key={option.key}
          option={option}
          isSelected={selectedKeys.includes(option.key)}
          isEnabled={isEnabled}
          onClick={() => onSelect(option.key)}
        />
      ))}
    </div>
  );
}

/** Grid selection differs from multiple choice only in what an option contains — a board, not a symbol. */
export const GridSelectionAnswer = MultipleChoiceAnswer;

function OptionTile({
  option,
  isSelected,
  isEnabled,
  onClick,
}: {
  option: ChallengeOption;
  isSelected: boolean;
  isEnabled: boolean;
  onClick: () => void;
}) {
  const symbol = option.symbol ?? null;
  const parsed = symbol === null ? null : parseSymbol(symbol);

  return (
    <button
      type="button"
      role="radio"
      aria-checked={isSelected}
      // §5: the tile announces its label, and „vybraté" when selected, which `aria-checked` supplies.
      //
      // It already carried `option.label`, so a tile was never nameless — but for a symbol tile the label
      // is „Možnosť 1", which names the button and not the thing on it. The server's description says
      // which shape it is (R0117).
      aria-label={
        option.accessibleName !== undefined && option.accessibleName.length > 0
          ? option.accessibleName
          : option.label
      }
      disabled={!isEnabled}
      onClick={onClick}
      style={{
        minHeight: 104,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 'var(--space-2)',
        borderRadius: 'var(--radius-m)',
        background: isSelected ? 'var(--surface-selected)' : 'var(--surface-raised)',
        border: isSelected ? '2px solid var(--border-selected)' : '1px solid var(--border-default)',
        padding: isSelected ? 'calc(var(--space-3) - 1px)' : 'var(--space-3)',
        transition: 'background var(--motion-quick) ease, border-color var(--motion-quick) ease',
      }}
    >
      {parsed === null ? (
        <AppText style="bodyL" align="center">
          {option.label}
        </AppText>
      ) : parsed.kind === 'grid' ? (
        <>
          <GridTokenView rows={parsed.rows} cellSize={32} />
          <AppText style="bodyS" color="var(--text-secondary)">
            {option.label}
          </AppText>
        </>
      ) : (
        <SymbolView token={symbol as string} size={56} />
      )}
    </button>
  );
}

export function TextAnswerControl({
  value,
  isEnabled,
  onChange,
}: {
  value: string;
  isEnabled: boolean;
  onChange: (value: string) => void;
}) {
  return (
    <AppTextField
      label={Strings.sessionAnswerLabel}
      value={value}
      onChange={(next: string) => {
        if (isEnabled) onChange(next);
      }}
    />
  );
}

/**
 * Ordering, by tapping.
 *
 * The child taps a word to add it to the sentence, and taps it in the sentence to take it back out. No drag
 * anywhere — the tap-only path is a requirement, not a fallback.
 */
export function OrderingAnswer({
  options,
  ordering,
  isEnabled,
  onChange,
}: {
  options: ChallengeOption[];
  ordering: string[];
  isEnabled: boolean;
  onChange: (ordering: string[]) => void;
}) {
  const remaining = options.filter((option: ChallengeOption): boolean => !ordering.includes(option.key));
  const labelOf = (key: string): string =>
    options.find((option: ChallengeOption): boolean => option.key === key)?.label ?? key;

  return (
    <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
      <div
        style={{
          width: '100%',
          minHeight: 'var(--touch-child)',
          borderRadius: 'var(--radius-m)',
          background: 'var(--surface-sunken)',
          padding: 'var(--space-3)',
          display: 'flex',
          flexWrap: 'wrap',
          gap: 'var(--space-2)',
          alignItems: 'center',
        }}
      >
        {ordering.length === 0 ? (
          <AppText style="bodyS" color="var(--text-secondary)">
            {Strings.sessionOrderingHint}
          </AppText>
        ) : (
          ordering.map((key: string) => (
            <WordChip
              key={key}
              label={labelOf(key)}
              isPlaced
              isEnabled={isEnabled}
              onClick={() => onChange(ordering.filter((entry: string): boolean => entry !== key))}
            />
          ))
        )}
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-2)' }}>
        {remaining.map((option: ChallengeOption) => (
          <WordChip
            key={option.key}
            label={option.label}
            isPlaced={false}
            isEnabled={isEnabled}
            onClick={() => onChange([...ordering, option.key])}
          />
        ))}
      </div>
    </div>
  );
}

function WordChip({
  label,
  isPlaced,
  isEnabled,
  onClick,
}: {
  label: string;
  isPlaced: boolean;
  isEnabled: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      disabled={!isEnabled}
      onClick={onClick}
      style={{
        minHeight: 'var(--touch-child)',
        borderRadius: 'var(--radius-pill)',
        background: isPlaced ? 'var(--surface-selected)' : 'var(--surface-raised)',
        border: `1px solid ${isPlaced ? 'var(--border-selected)' : 'var(--border-default)'}`,
        padding: 'var(--space-3) var(--space-4)',
        fontSize: '18px',
        lineHeight: '26px',
        fontWeight: 400,
      }}
    >
      {label}
    </button>
  );
}
